import { useCallback, useEffect, useId, useState } from 'react';

import { Badge, Button, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Select } from '@ds/forms';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { lerOfx, sugerirTitulos } from '@/data/financeiroAvulso';
import type { ContaNoCadastro, LinhaDoExtratoImportada, LinhaDoOfx } from '@/data/financeiroAvulso';
import type { Titulo } from '@/data/tipos';

export interface ConciliacaoModalProps {
    contas: ContaNoCadastro[];
    onFechar: () => void;
    onMudou: () => void;
}

/** O `FileReader` e não `File.text()`: é o que todo navegador e o jsdom têm. */
function lerArquivo(arquivo: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result ?? ''));
        leitor.onerror = () => reject(leitor.error ?? new Error('Não foi possível ler o arquivo.'));
        leitor.readAsText(arquivo);
    });
}

/**
 * Conciliação bancária por OFX.
 *
 * Três passos: ler o arquivo, gravar as linhas (o mesmo FITID não entra duas
 * vezes), e vincular cada linha a um título. A sugestão é por valor exato do que
 * falta e vencimento perto da data — mas quem vincula é uma pessoa, e cada
 * vínculo vira um pagamento de verdade. CSV e CNAB não entram aqui.
 */
export function ConciliacaoModal({ contas, onFechar, onMudou }: ConciliacaoModalProps) {
    const aviso = useAviso();
    const idConta = useId();
    const idArquivo = useId();

    const [contaId, setContaId] = useState(contas[0]?.id ?? '');
    const [lido, setLido] = useState<{ nome: string; linhas: LinhaDoOfx[]; semFitid: number; descartadas: number } | null>(null);
    const [importadas, setImportadas] = useState<LinhaDoExtratoImportada[]>([]);
    const [abertos, setAbertos] = useState<Titulo[]>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);

    const recarregar = useCallback(async () => {
        if (!contaId) return;
        try {
            const [linhas, financeiro] = await Promise.all([
                dados().extratoImportado(contaId),
                dados().financeiro({ aba: 'visao', porPagina: 1000 }),
            ]);
            setImportadas(linhas);
            setAbertos(financeiro.titulos.filter((t) => t.situacao === 'aberto' || t.situacao === 'parcial'));
            setErro(null);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler o extrato.');
        }
    }, [contaId]);

    useEffect(() => { void recarregar(); }, [recarregar]);

    const escolherArquivo = async (arquivo: File | undefined) => {
        if (!arquivo) return;
        try {
            const texto = await lerArquivo(arquivo);
            const resultado = lerOfx(texto);
            if (!resultado.linhas.length) {
                setLido(null);
                setErro('Nenhuma transação encontrada. O arquivo precisa ser um extrato OFX do banco.');
                return;
            }
            setLido({ nome: arquivo.name, ...resultado });
            setErro(null);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler o arquivo.');
        }
    };

    const importar = async () => {
        if (!lido || !contaId) return;
        setOcupado(true);
        try {
            const { novas, repetidas } = await dados().importarExtrato(contaId, lido.linhas);
            aviso({ title: 'Extrato importado', description: `${novas} ${novas === 1 ? 'linha nova' : 'linhas novas'}${repetidas ? ` · ${repetidas} já estavam importadas` : ''}` });
            setLido(null);
            await recarregar();
        } catch (e) {
            aviso({ title: 'Não foi possível importar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const agir = async (acao: () => Promise<void>, sucesso: string) => {
        setOcupado(true);
        try {
            await acao();
            aviso({ title: sucesso });
            await recarregar();
            onMudou();
        } catch (e) {
            aviso({ title: 'Não foi possível concluir', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const pendentes = importadas.filter((l) => l.situacao === 'pendente');
    const conciliadas = importadas.filter((l) => l.situacao === 'conciliada').length;
    const ignoradas = importadas.filter((l) => l.situacao === 'ignorada').length;

    return (
        <Modal
            open width={640} onClose={onFechar}
            title="Conciliação bancária" description="Importe o extrato OFX e vincule cada lançamento a um título."
            footer={<Button variant="secondary" onClick={onFechar}>Fechar</Button>}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FormField label="Conta" htmlFor={idConta} required>
                    <Select id={idConta} value={contaId} onChange={(v) => { setContaId(v); setLido(null); }} options={contas.map((c) => ({ value: c.id, label: c.nome }))} />
                </FormField>

                <FormField label="Extrato OFX" htmlFor={idArquivo} hint="O arquivo que o banco exporta. O mesmo arquivo importado de novo não duplica.">
                    <input
                        id={idArquivo} type="file" accept=".ofx,application/x-ofx,application/ofx"
                        onChange={(e) => { void escolherArquivo(e.target.files?.[0]); }}
                        style={{ fontFamily: 'var(--font-ui)', fontSize: 13 }}
                    />
                </FormField>

                {erro && <Alert tone="danger" title="Algo não deu certo">{erro}</Alert>}

                {lido && (
                    <Alert
                        tone="info" title={`${lido.linhas.length} ${lido.linhas.length === 1 ? 'transação' : 'transações'} em ${lido.nome}`}
                        action={<Button size="sm" onClick={() => { void importar(); }} disabled={ocupado}>Importar {lido.linhas.length} {lido.linhas.length === 1 ? 'linha' : 'linhas'}</Button>}
                    >
                        {lido.semFitid > 0 && `${lido.semFitid} sem identificador do banco: reimportar o mesmo arquivo pode duplicá-las. `}
                        {lido.descartadas > 0 && `${lido.descartadas} ignoradas por não terem data ou valor.`}
                    </Alert>
                )}

                <Divider label={`Pendentes · ${pendentes.length}`} spacing={2} />
                {pendentes.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                        Nada para conciliar nesta conta.{conciliadas || ignoradas ? ` ${conciliadas} conciliadas, ${ignoradas} ignoradas.` : ''}
                    </p>
                ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {pendentes.map((l) => {
                            const sugestoes = sugerirTitulos(l, abertos);
                            return (
                                <li key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '11px 13px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <span style={{ width: 84, fontSize: 13, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{dataSimples(l.data)}</span>
                                        <span style={{ flex: 1, minWidth: 160, fontSize: 13.5, fontWeight: 500 }}>{l.descricao || l.fitid}</span>
                                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: l.valorCentavos < 0 ? 'var(--danger-strong)' : 'var(--success-strong)' }}>
                                            {l.valorCentavos < 0 ? '−' : '+'}{moeda(Math.abs(l.valorCentavos))}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        {sugestoes.length === 0 ? (
                                            <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Nenhum título em aberto com esse valor perto dessa data.</span>
                                        ) : sugestoes.map((t) => (
                                            <Button
                                                key={t.id} size="sm" variant="secondary" iconLeft={<Icon name="link" size={13} />} disabled={ocupado}
                                                aria-label={`Conciliar com ${t.descricao} · ${t.contraparte}`}
                                                onClick={() => { void agir(() => dados().conciliarLinhaDoExtrato(l.id, t.id), 'Lançamento conciliado'); }}
                                            >
                                                {t.descricao} · venc. {dataSimples(t.vencimento)}
                                            </Button>
                                        ))}
                                        <span style={{ marginLeft: 'auto' }} />
                                        <Button size="sm" variant="ghost" disabled={ocupado} onClick={() => { void agir(() => dados().ignorarLinhaDoExtrato(l.id), 'Lançamento ignorado'); }}>
                                            Ignorar
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {(conciliadas > 0 || ignoradas > 0) && pendentes.length > 0 && (
                    <p style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        <Badge tone="success" dot>{conciliadas} conciliadas</Badge>
                        <Badge tone="neutral" dot>{ignoradas} ignoradas</Badge>
                    </p>
                )}

                <Alert tone="neutral" title="O que a conciliação faz" dismissible={false}>
                    Cada linha do extrato é comparada com os títulos em aberto pelo valor que falta e pela data. O vínculo só é
                    aplicado depois da sua confirmação, e vira um pagamento naquela conta e naquele dia.
                </Alert>
            </div>
        </Modal>
    );
}
